import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import { db } from '../../utils/firebase';
import {
  listChangedProjectNotificationPrefs,
  saveProjectNotificationPref,
  PROJECT_NOTIFICATION_MODE_OPTIONS,
  type ProjectNotificationMode,
  type ProjectNotificationPrefRow,
} from '../../utils/projectNotificationPrefs';

const ProjectNotificationPrefsList: React.FC = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<ProjectNotificationPrefRow[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      setRows(await listChangedProjectNotificationPrefs(db, user.id));
    } catch (err) {
      console.error('[ProjectNotificationPrefsList] load failed', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onChangeMode = async (projectId: string, mode: ProjectNotificationMode, projectName?: string | null) => {
    if (!user?.id) return;
    try {
      await saveProjectNotificationPref({
        db,
        userId: user.id,
        projectId,
        mode,
        projectName,
      });
      await reload();
    } catch (err) {
      console.error('[ProjectNotificationPrefsList] save failed', err);
      window.alert('Could not update project notification setting.');
    }
  };

  if (loading) {
    return <p className="settings-ops-hint">Loading project notification settings…</p>;
  }

  if (rows.length === 0) {
    return (
      <p className="settings-ops-hint">
        No per-project overrides yet. Open a project and use the Notifications control in the hub toolbar,
        or the ⋯ menu on a bell item.
      </p>
    );
  }

  return (
    <div className="proj-notif-settings-list">
      <p className="settings-ops-hint" style={{ marginBottom: 10 }}>
        Projects where you changed notification delivery. Switch back to All to restore defaults.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
        {rows.map((row) => (
          <li
            key={row.projectId}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gap: 12,
              alignItems: 'center',
              padding: '10px 12px',
              border: '1px solid var(--pm-border, #e2e0db)',
              borderRadius: 8,
            }}
          >
            <div>
              <Link to={`/project-hub/${row.projectId}`} style={{ fontWeight: 600 }}>
                {row.projectName || row.projectId}
              </Link>
              <div className="settings-ops-hint" style={{ marginTop: 2 }}>
                {PROJECT_NOTIFICATION_MODE_OPTIONS.find((o) => o.id === row.mode)?.hint}
              </div>
            </div>
            <select
              aria-label={`Notification mode for ${row.projectName || row.projectId}`}
              value={row.mode}
              onChange={(e) =>
                void onChangeMode(
                  row.projectId,
                  e.target.value as ProjectNotificationMode,
                  row.projectName,
                )
              }
            >
              {PROJECT_NOTIFICATION_MODE_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ProjectNotificationPrefsList;
